import React from "react";
import { Link } from "react-router-dom";
import { Moon, PanelLeft, Sun, Trash2 } from "lucide-react";
import { SVGAdiona, SVGCross, SVGPlus } from "../assets/PackageSVG";
import { SidebarProps } from "../types/sidebar";
import styles from "./SideBarLeft.module.scss";
import { UserProfile } from "./UserProfile";
import { useTheme } from "../context/ThemeContext";

// Old static mock data for the sidebar, replaced by real chat history
// (ChatHistoryItem[] passed in via props). Kept in case "Saved" trips —
// which have no real data source yet — comes back as a feature.
// import {SVGHeart, SVGClock} from "../assets/PackageSVG";
// import {SavedRoutes} from "../types/sidebar";
// const SAVED_ROUTES: SavedRoutes = {
//     recent: [
//         { id: 1, dest: 'Tokyo, Japan', dates: 'Jun 12 – Jun 19', price: '$1,350', icon: '🗼' },
//         { id: 2, dest: 'Barcelona, Spain', dates: 'Jul 4 – Jul 10', price: '$890', icon: '🏖' },
//         { id: 3, dest: 'Banff, Canada', dates: 'Aug 1 – Aug 7', price: '$650', icon: '🏔' },
//     ],
//     saved: [
//         { id: 4, dest: 'Santorini, Greece', dates: 'Sep 15 – Sep 22', price: '$1,200', icon: '🌅', hearted: true },
//         { id: 5, dest: 'Bali, Indonesia', dates: 'Oct 1 – Oct 8', price: '$890', icon: '🌴', hearted: true },
//     ],
// };
// {/* Saved routes JSX previously rendered here — see step-1 instructions doc */}

const formatChatDate = (updatedAt: number) =>
    new Date(updatedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

export const SideBarLeft: React.FC<SidebarProps> = ({
    collapsed,
    onToggle,
    onNewChat,
    onMobileClose,
    isMobile,
    chats,
    activeChatId,
    onChatSelect,
    onChatDelete,
}) => {
    const { theme, toggleTheme } = useTheme();
    const themeLabel = theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme';
    const ThemeIcon = theme === 'dark' ? Sun : Moon;

    const selectChat = (id: string) => {
        onChatSelect?.(id);
        onMobileClose();
    };

    return (
        <aside className={`${styles.cpSidebar}${collapsed ? ` ${styles.collapsed}` : ''}`}>
            <div className={styles.cpSbTop}>
                <div className={styles.cpSbLogoRow}>
                    <Link to="/" className={styles.cpSbLogo} title="Adiona">
                        <SVGAdiona />
                        {!collapsed && <span className={styles.cpSbLogoName}>Adiona</span>}
                    </Link>
                    {/* The chat page has no Header of its own, so this row is
                        the only place the theme can be switched — it has to
                        survive on mobile, where the collapse toggle doesn't
                        (a drawer that slides away has nothing to collapse to)
                        and a close button takes its place. */}
                    {!collapsed && (
                        <div className={styles.cpSbTopActions}>
                            <button
                                className={styles.cpSbToggle}
                                onClick={toggleTheme}
                                aria-label={themeLabel}
                                title={themeLabel}
                            >
                                <ThemeIcon size={16} aria-hidden="true" />
                            </button>
                            {isMobile ? (
                                <button
                                    className={styles.cpSbClose}
                                    onClick={onMobileClose}
                                    aria-label="Close trips"
                                    title="Close"
                                >
                                    <SVGCross width="16" height="16" />
                                </button>
                            ) : (
                                <button
                                    className={styles.cpSbToggle}
                                    onClick={onToggle}
                                    aria-label="Collapse sidebar"
                                    title="Collapse sidebar"
                                >
                                    <PanelLeft size={16} aria-hidden="true" />
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* Collapsed: the toggles drop below the logo instead of
                    sharing its row, since there's no room for a row anymore. */}
                {!isMobile && collapsed && (
                    <div className={styles.cpSbToggleRail}>
                        <button
                            className={styles.cpSbToggle}
                            onClick={toggleTheme}
                            aria-label={themeLabel}
                            title={themeLabel}
                        >
                            <ThemeIcon size={16} aria-hidden="true" />
                        </button>
                        <button
                            className={styles.cpSbToggle}
                            onClick={onToggle}
                            aria-label="Expand sidebar"
                            title="Expand sidebar"
                        >
                            <PanelLeft size={16} aria-hidden="true" />
                        </button>
                    </div>
                )}

                <button className={styles.cpSbNew} onClick={onNewChat}>
                    <SVGPlus width="16" height="16" />
                    {!collapsed && <span>New trip</span>}
                </button>
            </div>

            {!collapsed && (
                <div className={styles.cpSbRoutes}>
                    <div className={styles.cpSbGroup}>
                        <div className={styles.cpSbGroupLabel}>Recent</div>
                        {chats.length === 0 && (
                            <p className={styles.cpSbEmpty}>Trips you start are saved here.</p>
                        )}
                        {chats.map((chat) => (
                            <div key={chat.id} className={styles.cpSbRouteRow}>
                                <button
                                    className={`${styles.cpSbRoute}${activeChatId === chat.id ? ` ${styles.cpSbRouteActive}` : ''}`}
                                    onClick={() => selectChat(chat.id)}
                                    title={chat.title}
                                >
                                    <div className={styles.cpSbRouteInfo}>
                                        <span className={styles.cpSbRouteDest}>{chat.title}</span>
                                        <span className={styles.cpSbRouteMeta}>{formatChatDate(chat.updatedAt)}</span>
                                    </div>
                                </button>
                                {onChatDelete && (
                                    <button
                                        type="button"
                                        className={styles.cpSbRouteDelete}
                                        onClick={() => onChatDelete(chat.id)}
                                        aria-label={`Delete chat: ${chat.title}`}
                                        title="Delete chat"
                                    >
                                        <Trash2 size={14} aria-hidden="true" />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <UserProfile collapsed={collapsed} />
        </aside>
    );
}
